/**
 * Single source of truth for per-user doctype upload permissions.
 *
 * Imported by:
 *   - server/middleware/validateFileContent.js  (enforcement, authoritative)
 *   - src/components/…                          (filtering the doctype <select>)
 *
 * Both sides MUST use this module. A divergent copy would let the dropdown
 * offer a type the server then rejects.
 *
 * Stored doctypes are full labels ("MA Montageanleitung"), but the admin UI
 * teaches codes ("MA,BA,E") via its placeholder — so an entry matches either
 * the full label or the leading code token.
 */

/** Trim, collapse inner whitespace, lowercase (German-aware). */
function normalize(value) {
  return String(value ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('de');
}

/** Leading whitespace-separated token: "EE-KE CE Einbau-…" → "ee-ke". */
function codeOf(value) {
  return normalize(value).split(' ')[0];
}

/**
 * Is `doctype` covered by `allowedDoctypes`?
 *
 * `null`/`undefined` → unrestricted (the default for every user).
 * `[]`               → ALSO unrestricted, deliberately: AdminPage collapses an
 *                      empty text field to null, so `[]` is unreachable from the
 *                      UI. Treating it as deny-all would brick a user in a state
 *                      the admin can neither see nor clear.
 *
 * Matching is token EQUALITY, never a prefix — otherwise "E" would match
 * "EE-KE CE Einbau- und Konformitätserklärung".
 */
export function isDoctypeAllowed(doctype, allowedDoctypes) {
  if (!Array.isArray(allowedDoctypes) || allowedDoctypes.length === 0) return true;

  const wanted = normalize(doctype);
  if (!wanted) return false; // restricted user must name a type — no blank bypass

  const wantedCode = codeOf(doctype);
  return allowedDoctypes.some((allowed) => {
    const a = normalize(allowed);
    return a === wanted || (!!a && codeOf(allowed) === wantedCode);
  });
}

/** True when the user may upload at least one type. */
export function hasAnyDoctypeAllowed(allowedDoctypes) {
  return !Array.isArray(allowedDoctypes) || allowedDoctypes.length === 0
    ? true
    : allowedDoctypes.some((a) => !!normalize(a));
}
